import React, { memo, useCallback, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import {
  beginDatasetDrag,
  beginDatasetDragWith,
  createOsmGroupDragPayload,
  endDatasetDrag,
  writeDatasetDragData,
  DATASET_FORMAT_LABEL,
  layerGroupFormat,
  DatasetCatalogItem,
  datasetDisplayTitle,
  datasetSubtitle,
  datasetProvenanceLabel,
  type DatasetPaletteGroup,
} from "../../../../services/datasetCatalog";
import {
  datasetCountCompact as datasetCount,
  relativeTimeOrEmpty as relativeTime,
} from "../../../datasets/catalog/datasetDetailHelpers";
import packageStyles from "../toolsMenuPackagePalette/ToolsMenuPackagePalette.module.css";
import rowStyles from "./DatasetPaletteRows.module.css";

import { DatasetConnectionBadge } from "../../../datasets/catalog/DatasetConnectionBadge";
import { useReactFlow } from "reactflow";
import { isNodeLinkedToAnyDataset } from "../../../../services/datasetCatalog";
import { focusLinkedNodes } from "../../../../utils/focusDatasetNodes";
import { useNotebookViewContext } from "../../../../providers/flow/notebookViewContext";
import { useToastContext } from "../../../../providers/ToastProvider";
import { CopyButton } from "../../../CopyButton";
import { DetailsButton } from "../../../DetailsButton";
import { useDatasetDetails } from "../../../datasets/catalog/datasetDetailsContext";
import { datasetReferenceCode } from "../../../../services/datasetCatalog";


/**
 * An id with a break opportunity after each `.` and before its `@version`, so a
 * long one wraps where it reads instead of losing the version (#527).
 */
export function breakableId(text: string | null | undefined): React.ReactNode {
  if (!text) return text;
  return text.split(/([.@])/).map((part, index) => {
    if (part === ".") return <React.Fragment key={index}>.<wbr /></React.Fragment>;
    if (part === "@") return <React.Fragment key={index}><wbr />@</React.Fragment>;
    return part;
  });
}

export const DatasetRow = memo(function DatasetRow({
  dataset,
}: {
  dataset: DatasetCatalogItem;
}) {
  const count = datasetCount(dataset);
  const time = relativeTime(dataset.updatedAt);
  const metaParts = [count, time].filter(Boolean).join(" · ");
  const tooltipParts = [datasetDisplayTitle(dataset), metaParts].filter(Boolean);
  const formatChipClass =
    rowStyles[`chip_${dataset.format}` as keyof typeof rowStyles] ?? rowStyles.formatChip;

  const reactFlow = useReactFlow();
  const { reveal } = useNotebookViewContext();
  const { showToast } = useToastContext();
  const { openDatasetDetails } = useDatasetDetails();

  const selectOnCanvas = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      // A node is linked when it references this dataset by any means:
      //  - created from the palette (datasetSource) or dropped onto it
      //    (datasetRefs / appliedDatasets) - including a group-created node that
      //    references this layer among its members;
      //  - producer: the node that generated this computed dataset.
      const isLinked = (n: { id: string; data: any }) =>
        isNodeLinkedToAnyDataset(n.data, [dataset.id]) || n.id === dataset.producerNodeId;
      if (focusLinkedNodes(reactFlow, isLinked, reveal) === 0) {
        showToast("No nodes on the canvas use this dataset", "info");
      }
    },
    [dataset.id, dataset.producerNodeId, reactFlow, showToast, reveal],
  );

  return (
    // Its tooltip opens to the right, as every tool rail palette row's does.
    <div
      className={packageStyles.packageKindRow}
      data-dataset-id={dataset.id}
      data-tooltip={tooltipParts.join(" · ")}
      data-tooltip-side="right"
    >
      <div
        className={`${packageStyles.packageKindRowDrag} ${rowStyles.datasetRowDrag}`}
        draggable
        // An icon and a format chip: the drag handle is named for what it does.
        aria-label={`Drag ${datasetDisplayTitle(dataset)} onto the canvas`}
        onDragStart={(event) => {
          writeDatasetDragData(event.dataTransfer, beginDatasetDrag(dataset));
        }}
        onDragEnd={() => endDatasetDrag()}
      >
        <FontAwesomeIcon
          icon={faDatabase}
          className={`${packageStyles.packageKindDragIcon} ${rowStyles.datasetDragIcon}`}
        />
        <span className={`${rowStyles.iconBadge} ${formatChipClass}`}>
          {DATASET_FORMAT_LABEL[dataset.format]}
        </span>
      </div>
      <button type="button" className={packageStyles.packageKindRowMeta} onClick={selectOnCanvas}>
        <span className={rowStyles.datasetRowTitle}>{datasetDisplayTitle(dataset)}</span>
        <span className={rowStyles.datasetRowId}>{breakableId(datasetSubtitle(dataset))}</span>

        <div className={rowStyles.rowMeta}>
          <span className={packageStyles.packageKindCategoryChip}>
            {datasetProvenanceLabel(dataset.origin, dataset.format)}
          </span>

          {metaParts ? <span className={rowStyles.rowMetaText}>{metaParts}</span> : null}
          <DatasetConnectionBadge dataset={dataset} className={rowStyles.connBadge} />
        </div>
      </button>
      {/* The rail sits beside the code editor, so this is where someone
          writing a node needs the reference. It is outside the meta button
          because that button selects the dataset's nodes on the canvas
          (#206). */}
      <CopyButton
        value={datasetReferenceCode(dataset)}
        label="Copy dataset reference"
        className={rowStyles.copyButton}
      />
      {/* The way into the dataset's details, as every catalog card offers. */}
      <DetailsButton
        label={`View ${datasetDisplayTitle(dataset)} details`}
        className={rowStyles.detailsButton}
        onClick={() => openDatasetDetails(dataset.id, { fallbackDataset: dataset })}
      />
    </div>
  );
});

/**
 * Collapsible parent row for a multilayer OSM PBF import. The header is not
 * draggable (it represents the whole import); expanding it reveals the
 * individual layer datasets, each an ordinary - still draggable - {@link
 * DatasetRow}. Grouping is computed upstream by ``groupDatasetsForPalette``.
 */
export const DatasetGroupRow = memo(function DatasetGroupRow({
  group,
}: {
  group: DatasetPaletteGroup;
}) {
  const [open, setOpen] = useState(false);
  const layerCount = group.members.length;
  const time = relativeTime(group.updatedAt);
  // An OSM PBF or a GeoPackage import, as its id says, or the layers of one
  // Discovery download, which say their own format.
  const groupFormat = layerGroupFormat(group);
  const formatLabel = DATASET_FORMAT_LABEL[groupFormat];
  const formatChipClass = rowStyles[`chip_${groupFormat}`] ?? rowStyles.formatChip;
  // Dragging the parent creates one node loading ALL layers (the full import).
  const dragPayload = useMemo(() => createOsmGroupDragPayload(group), [group]);

  const reactFlow = useReactFlow();
  const { reveal } = useNotebookViewContext();
  const { showToast } = useToastContext();

  // Highlight every node linked to this import: any node referencing a member
  // layer (individual-layer or dropped-on nodes), the full-group node (its
  // datasetSource is the group id), or a layer's producer. Mirrors the single
  // DatasetRow highlight so the parent behaves consistently.
  const selectOnCanvas = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const linkIds = [group.groupId, ...group.members.map((m) => m.id)];
      const producerIds = new Set(
        group.members.map((m) => m.producerNodeId).filter(Boolean) as string[],
      );
      const isLinked = (n: { id: string; data: any }) =>
        isNodeLinkedToAnyDataset(n.data, linkIds) || producerIds.has(n.id);
      if (focusLinkedNodes(reactFlow, isLinked, reveal) === 0) {
        showToast("No nodes on the canvas use this dataset", "info");
      }
    },
    [group.groupId, group.members, reactFlow, showToast, reveal],
  );

  return (
    <div className={rowStyles.groupBlock} data-osm-group-id={group.groupId}>
      {/* The tooltip is the header's, so an expanded member shows its own, and
          the drag handle, the nearer target, says what dragging it does. */}
      <div
        className={rowStyles.groupHeader}
        data-dataset-id={group.groupId}
        data-tooltip={`${group.title} · ${formatLabel} · ${layerCount} layer${layerCount === 1 ? "" : "s"}`}
        data-tooltip-side="right"
      >
        <div
          className={`${packageStyles.packageKindRowDrag} ${rowStyles.datasetRowDrag} ${rowStyles.groupIconBox}`}
          draggable
          onDragStart={(event) => {
            writeDatasetDragData(event.dataTransfer, beginDatasetDragWith(dragPayload));
          }}
          onDragEnd={() => endDatasetDrag()}
          aria-label={`Drag to add all ${layerCount} layer${layerCount === 1 ? "" : "s"} as one dataset`}
          data-tooltip={`Drag to add all ${layerCount} layer${layerCount === 1 ? "" : "s"} as one dataset`}
          data-tooltip-side="right"
        >
          <FontAwesomeIcon
            icon={faDatabase}
            className={`${packageStyles.packageKindDragIcon} ${rowStyles.datasetDragIcon}`}
          />
          <span className={`${rowStyles.iconBadge} ${formatChipClass}`}>
            {formatLabel}
          </span>
        </div>
        {/* Meta area highlights linked nodes (like a single row); the caret
            button owns expand/collapse so the two actions don't conflict. */}
        <button
          type="button"
          className={rowStyles.groupMetaButton}
          onClick={selectOnCanvas}
        >
          <span className={packageStyles.packageKindRowLabel}>{group.title}</span>
          <div className={rowStyles.rowMeta}>
            <span className={packageStyles.packageKindCategoryChip}>Imported</span>
            {time ? <span className={rowStyles.rowMetaText}>{time}</span> : null}
          </div>
        </button>
        <button
          type="button"
          className={rowStyles.groupCaretButton}
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${group.title}: ${formatLabel} import with ${layerCount} layer${layerCount === 1 ? "" : "s"}`}
        >
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className={rowStyles.groupCaret}
          />
        </button>
      </div>
      {open ? (
        <div className={rowStyles.groupMembers} role="group" aria-label={`${group.title} layers`}>
          {group.members.map((member) => (
            <DatasetRow
              key={`${member.origin}:${member.id}`}
              dataset={member}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
});
